import { useState, forwardRef } from 'react';
import type {
   ComponentType,
   PropsWithChildren,
   InputHTMLAttributes,
   MouseEvent as ReactMouseEvent,
   CSSProperties,
   ReactNode,
} from 'react';
import { initializeChakraComponent } from 'Shared/chakra-initialize';
import { withBreakpointsContext } from 'Shared/utils/breakpoints';
import { _js } from '@ifixit/localize';
import styled from 'styled-components';
import { color, space, fontSize, shadow, borderRadius } from '@ifixit/primitives';
import {
   Box,
   Image,
   Text,
   Heading,
   InputRightElement,
   InputGroup,
   LinkBox,
   LinkOverlay,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import LoadingCircle from 'Shared/icons/loading-circle.svg';
import { multiSearchAlgolia } from 'Guide/Search/multisearch_algolia';
import { useCombobox } from 'downshift';
import { getSearchClient, SearchClientType } from 'Guide/searchClient';
import { getInputFormValue, setInputFormValue } from 'Shared/ifixit_store';
import { getNewPageURL } from 'Guide/get_new_page_url';

const HITS_PER_PAGE = 5;

/**
 * This is the hit result that is returned from Algolia.
 * It is a combination of only the attributes we use.
 */
export type HitType = {
   device: string;
   title: string;
   url: string;
   objectID: string;
   image_url: string;
   description: string;
};

/*
 * This is a custom component that is used to render the wiki search results as the user types.
 * This component runs off of algolia instantsearch, but can render a functional input form if
 * algolia is not set up on the site.
 * @param namespace - the namespace to search in
 * @param placeHolder - the placeholder text to display in the search box
 * @param buttonType - the text to display on the search button or 'none' to not display a button
 **/
export function WikiSearch({
   namespace,
   placeHolder,
   buttonType,
   createType,
   formTabIndex,
   buttonTabIndex,
   styledSearchBarContainer,
   styledSearchResultsContainer,
   inputClassName,
   isInvalidTitle = false,
   onNewDeviceSelected,
   onKnownDeviceSelected,
   onFocus,
   onBlur,
}: {
   namespace: string;
   placeHolder: string;
   buttonType: string;
   createType?: string;
   formTabIndex?: number;
   buttonTabIndex?: number;
   /* eslint-disable-next-line @typescript-eslint/no-empty-object-type */
   styledSearchBarContainer?: ComponentType<PropsWithChildren<{}>>;
   /* eslint-disable-next-line @typescript-eslint/no-empty-object-type */
   styledSearchResultsContainer?: ComponentType<PropsWithChildren<{}>>;
   inputClassName?: string;
   isInvalidTitle?: boolean;
   onNewDeviceSelected: onSubmitType;
   onKnownDeviceSelected: onClickType;
   onFocus?: () => void;
   onBlur?: (e: any) => void;
}) {
   const langId = App.lang;
   const localIndex = App.isDev ? 'dev_' + namespace + '_' + langId : namespace + '_' + langId;
   const enIndex = App.isDev ? 'dev_' + namespace + '_en' : namespace + '_en';

   if (!createType) {
      createType = namespace;
   }
   const [queryResults, setQueryResults] = useState<HitType[]>([]);
   const [loading, setLoading] = useState(false);
   const searchClient: SearchClientType = getSearchClient();
   const SearchBarContainer = styledSearchBarContainer || DefaultSearchBarContainer;

   async function updateResults(query: string) {
      await multiSearch(query, localIndex, enIndex, searchClient).then(results => {
         setQueryResults(results);
         setLoading(false);
      });
   }

   const {
      isOpen,
      getMenuProps,
      getInputProps,
      highlightedIndex,
      getItemProps,
      setInputValue,
      inputValue,
   } = useCombobox({
      items: queryResults,
      // @ts-expect-error TS(2322) FIXME: Type '(item: HitType) => string' is not assignable... Remove this comment to see the full error message
      itemToString: (item: HitType) => (item ? item.title : ''),
      onInputValueChange: ({ inputValue }: { inputValue?: string }) => {
         // @ts-expect-error TS(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
         setInputFormValue(inputValue);
         // @ts-expect-error TS(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
         setInputValue(inputValue);
         if (inputValue) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME: Promises must be awaited, end with...
            updateResults(inputValue);
         }
      },
      onSelectedItemChange: ({ selectedItem }: { selectedItem?: HitType }) => {
         if (selectedItem) {
            onKnownDeviceSelected({ selectedItem, createType });
         }
      },
   });

   const SearchResultsContainer:
      | typeof DefaultSearchResultsContainer
      | typeof styledSearchResultsContainer =
      (isOpen && queryResults.length > 0) || loading
         ? styledSearchResultsContainer || DefaultSearchResultsContainer
         : DisplayNone;

   return (
      <div>
         <SearchBarContainer>
            <CustomSearchBox
               {...getInputProps()}
               setInputValue={setInputValue}
               onFocus={onFocus}
               onBlur={onBlur}
               placeholder={placeHolder}
               inputClassName={inputClassName}
               formType={'search'}
               tabIndex={formTabIndex}
               isInvalidTitle={isInvalidTitle}
            />
            <CreateButton
               namespace={createType}
               buttonType={buttonType}
               handleSubmit={onNewDeviceSelected}
               tabIndex={buttonTabIndex}
               query={inputValue}
            />
         </SearchBarContainer>
         <SearchResultsContainer>
            <ConditionalLoadingCircle loading={loading} />
            <NoStyleList {...getMenuProps()}>
               {isOpen &&
                  queryResults.map((item, index) => (
                     <HitItem
                        hit={item}
                        handleDropdownClick={() => {
                           onKnownDeviceSelected({ selectedItem: item, createType });
                        }}
                        createType={createType}
                        style={highlightedIndex === index ? { backgroundColor: '#bde4ff' } : {}}
                        key={`${item.title}${index}`}
                        {...getItemProps({ item, index })}
                     />
                  ))}
            </NoStyleList>
         </SearchResultsContainer>
      </div>
   );
}

async function multiSearch(
   query: string,
   localIndex: string,
   enIndex: string,
   searchClient: SearchClientType
): Promise<any> {
   const indices = localIndex === enIndex ? [localIndex] : [localIndex, enIndex];
   const results = await multiSearchAlgolia<HitType>(searchClient, indices, query, HITS_PER_PAGE);
   return results;
}

function ConditionalLoadingCircle({ loading }: { loading: boolean }) {
   if (!loading) {
      return null;
   }
   return (
      <span>
         <LoadingCircle />
      </span>
   );
}

interface CustomSearchBoxProps extends InputHTMLAttributes<HTMLInputElement> {
   placeHolder: string;
   inputClassName?: string;
   formType: string;
   tabIndex?: number;
   isInvalidTitle: boolean;
   setInputValue: (value: string) => void;
}

const CustomSearchBox = forwardRef<HTMLInputElement, CustomSearchBoxProps>((props, ref) => {
   const input = props.value || getInputFormValue() || '';
   const { isInvalidTitle, formType, inputClassName, setInputValue, ...otherProps } = props;
   return (
      <InputGroup>
         <PageTypeTitleInput
            $isInvalidTitle={isInvalidTitle}
            className={inputClassName + ' introDevice'}
            tabIndex={props.tabIndex}
            required
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            placeholder={_js(props.placeHolder)}
            spellCheck="false"
            maxLength={512}
            type={formType}
            title=""
            value={input}
            ref={ref}
            {...otherProps}
         />
         <InputRightElement
            right="2px"
            width="30px"
            height="100%"
            paddingRight="10px"
            display={input ? 'flex' : 'none'}
         >
            <ClearButton
               onMouseDown={(event: ReactMouseEvent<HTMLDivElement, MouseEvent>) => {
                  event.preventDefault();
                  setInputValue('');
                  setInputFormValue('');
               }}
            >
               <FontAwesomeIcon icon={faXmark} className="fa-solid" />
            </ClearButton>
         </InputRightElement>
      </InputGroup>
   );
});

CustomSearchBox.displayName = 'CustomSearchBox';

/**
 * Provides similar functionality to less' darken() function.
 */
function darkenColor({ hex, amt }: { hex: string; amt: number }) {
   hex = hex.slice(1);
   let color: number = Number.parseInt(hex, 16);
   color =
      ((color & 0x00_00_ff) + amt) |
      ((((color >> 8) & 0x00_ff) + amt) << 8) |
      (((color >> 16) + amt) << 16);
   return '#' + color.toString(16);
}

function CreateButton({
   namespace,
   buttonType,
   handleSubmit,
   tabIndex,
   query,
}: {
   namespace: string;
   buttonType: string;
   handleSubmit: onSubmitType;
   tabIndex?: number;
   query: string;
}) {
   if (buttonType.toLowerCase() === 'none') {
      return null;
   }
   return (
      <StyledButton
         tabIndex={tabIndex}
         className="create-button"
         onClick={(e: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.preventDefault();
            handleSubmit({ query, namespace });
         }}
      >
         {_js(buttonType)}
      </StyledButton>
   );
}

type HitProps = {
   hit: HitType;
   handleDropdownClick: onClickType;
   createType: string;
   query?: string;
   style: CSSProperties;
   children?: ReactNode;
};

type onSubmitType = ({ query, namespace }: { query?: string; namespace?: string }) => void;

type onClickType = ({
   selectedItem,
   query,
   createType,
}: {
   selectedItem: HitType;
   query?: string;
   createType: string;
}) => void;

const HitItem = forwardRef<HTMLLIElement, HitProps>(function HitItem(
   { hit: selectedItem, handleDropdownClick, createType, query, style }: HitProps,
   ref
) {
   return (
      <LinkBox>
         <ListItemWrapper
            ref={ref}
            onClick={e => {
               e.preventDefault();
               handleDropdownClick({
                  selectedItem,
                  query,
                  createType,
               });
            }}
            className="dropdown-hit-item"
            style={style}
         >
            <LinkOverlay
               href={getNewPageURL(createType, selectedItem.device, selectedItem.url)}
               data-device={selectedItem.device}
            ></LinkOverlay>
            <Box width="64px">
               <Image
                  src={selectedItem.image_url}
                  alt={selectedItem.title + ' search result image'}
                  flex="1"
                  width="100%"
                  p="10px 4px 4px 8px"
               />
            </Box>
            <Box flex="1" p="8px" width="calc(100% - 64px)">
               <Heading
                  fontSize={'md'}
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  color="@color-gray-900"
                  mt={0}
                  border="none"
               >
                  {selectedItem.title}
               </Heading>
               <Text mt={0} color="@color-gray-600" noOfLines={2}>
                  {selectedItem.description}
               </Text>
            </Box>
         </ListItemWrapper>
      </LinkBox>
   );
});

const StyledButton = styled.button`
   flex: 0 0 auto;
   margin-left: -1px;
   border: 1px solid ${color.brand[500]};
   border-radius: ${borderRadius['md']};
   border-top-left-radius: 0;
   border-bottom-left-radius: 0;
   color: white;
   background-color: ${color.brand[500]};
   position: relative;
   cursor: pointer;
   display: inline-block;
   font-size: ${fontSize['md']};
   text-align: center;
   height: 100%;
   // Mobile Safari button-style override.
   -webkit-appearance: none;
   &:focus {
      box-shadow: ${shadow[1]} ${color.brand[500]};
   }
   &:active {
      background-color: ${darkenColor({ hex: color.brand[500], amt: -10 })};
      border-color: ${darkenColor({ hex: color.brand[500], amt: -10 })};
   }
   &:hover {
      background-color: ${darkenColor({ hex: color.brand[500], amt: -5 })};
      border-color: ${darkenColor({ hex: color.brand[500], amt: -5 })};
   }
`;

const DefaultSearchBarContainer = styled.div`
   display: flex;
   width: 100%;
   height: ${space[7]};
`;

const DefaultSearchResultsContainer = styled.div`
   border: ${color.gray[400]} 1px solid;
   box-shadow: ${shadow[3]};
   display: block;
   &:empty {
      border: none;
   }
`;

const ListItemWrapper = styled.li`
   display: flex;
   margin-bottom: 0 !important;
   background-color: white;
   max-width: 100%;
   max-height: 80px;
   list-style-type: none;
   &:hover {
      background-color: ${color.gray[100]};
      text-decoration: none;
   }
`;

const PageTypeTitleInput = styled.input<{ $isInvalidTitle: boolean }>`
   flex: 1 0 auto;
   width: 100%;
   height: 100%;
   padding: ${space[2]} ${space[3]};
   margin: ${space[0]};
   font-size: ${space[4]};
   border: 1px solid rgba(0, 0, 0, 0.38);
   border-radius: ${borderRadius['md']} 0 0 ${borderRadius['md']};
   outline: none;
   background-color: ${props => (props.$isInvalidTitle ? 'rgb(239, 208, 208)' : '#ffffff')};
   color: ${color.black};
   &:focus {
      border-color: ${color.brand[500]};
      box-shadow: ${space[0]} ${space[0]} ${space[1]} ${color.brand[500]};
   }
`;

const ClearButton = styled.div`
   width: 20px;
   height: 20px;
   flex: 0;
   padding-right: ${space[1]};
   padding-left: ${space[1]};
   background-color: ${color.gray[50]};
   cursor: pointer;
`;

const DisplayNone = styled.div`
   display: none;
`;

const NoStyleList = styled.ul`
   list-style-type: none;
   padding: 0;
   margin: 0;
`;

initializeChakraComponent('WikiSearch', withBreakpointsContext(WikiSearch));
